import React, { Component, Fragment } from "react";
import Breadcrumb from "../common/breadcrumb";
import { connect } from "react-redux";
import { firestore } from "../../firebase/firebase.utils";
import { ShoppingBag, Package, MapPin, Phone, Mail, Calendar, User } from "react-feather";
import EmptyState from "../common/EmptyState";
import { toneFor, validId, avatarLetter } from "./usersDatatable";
import "../1688-taobao-orders/orders-kido.css";
import "../refund-requests/refunds-kido.css";
import "./users-kido.css";

// Order / booking status → playroom tone (.kd-st--* in orders-kido.css).
const STATUS_TONES = {
  Processing: "lilac",
  Confirmed: "butter",
  Packing: "peach",
  "On the way": "sky",
  Delivered: "mint",
  "Partial Delivered": "butter",
  Cancelled: "rose",
  Paid: "mint",
  Pending: "butter",
};

function StatusBadge({ status }) {
  return (
    <span className={`kd-status-badge kd-st--${STATUS_TONES[status] || "ink"}`}>
      <span className="kd-status-dot" />
      {status || "—"}
    </span>
  );
}

function LoadingRows() {
  return (
    <div className="kd-empty-state" role="status">
      <span className="kd-hop" aria-hidden="true"><i /><i /><i /></span>
      <p>Loading…</p>
    </div>
  );
}

const TABS = ["orders", "preorders", "addresses"];
const TAB_INFO = {
  orders: { label: "Orders", tone: "sky", Icon: ShoppingBag },
  preorders: { label: "Pre-orders", tone: "lilac", Icon: Package },
  addresses: { label: "Addresses", tone: "peach", Icon: MapPin },
};

const orderTotal = (order) =>
  (order.subTotal || 0) +
  (order.deliveryCharge || 0) -
  (order.discountApplied || 0) -
  (order.couponApplied ? order.couponApplied.discount || 0 : 0) -
  (order.finalDiscount || 0);

export class DetailUser extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: "orders",
      orders: [],
      preOrders: [],
      ordersLoading: true,
      preOrdersLoading: true,
    };
  }

  componentDidMount = async () => {
    const userId = this.props.match.params.userId;
    this.fetchOrders(userId);
    this.fetchPreOrders(userId);
  };

  fetchOrders = async (userId) => {
    try {
      const snap = await firestore
        .collection("orders")
        .where("userId", "==", userId)
        .orderBy("createdAt", "desc")
        .limit(20)
        .get();
      const orders = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      this.setState({ orders, ordersLoading: false });
    } catch (e) {
      console.error("Orders fetch error:", e);
      this.setState({ ordersLoading: false });
    }
  };

  fetchPreOrders = async (userId) => {
    try {
      const snap = await firestore
        .collection("bookingRequest")
        .where("userId", "==", userId)
        .orderBy("time", "desc")
        .limit(20)
        .get();
      const preOrders = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      this.setState({ preOrders, preOrdersLoading: false });
    } catch (e) {
      console.error("Pre-orders fetch error:", e);
      this.setState({ preOrdersLoading: false });
    }
  };

  formatDate(createdAt) {
    if (!createdAt) return "—";
    const secs = createdAt.seconds || createdAt._seconds;
    if (!secs) return "—";
    return new Date(secs * 1000).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  formatJoinDate(createdAt) {
    if (!createdAt) return "—";
    const secs = createdAt.seconds || createdAt._seconds;
    if (!secs) return "—";
    return new Date(secs * 1000).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  render() {
    const { user } = this.props;
    const { activeTab, orders, preOrders, ordersLoading, preOrdersLoading } = this.state;

    const addresses = (() => {
      if (!user || !user.address) return [];
      if (Array.isArray(user.address)) return user.address;
      return [];
    })();

    const name = user && user.displayName ? user.displayName : "Customer";
    const initial = avatarLetter(name);
    const tone = toneFor(user ? user.displayName || user.uid : "?");
    const mobile = user ? user.mobileNo || user.mobileNumber : null;
    const spent = orders
      .filter((o) => o.orderStatus !== "Cancelled")
      .reduce((sum, o) => sum + orderTotal(o), 0);
    const counts = {
      orders: orders.length,
      preorders: preOrders.length,
      addresses: addresses.length,
    };

    return (
      <Fragment>
        <Breadcrumb title={user ? user.displayName : "Customer"} parent="Customers" />
        <div className="container-fluid">

          {/* Profile: a big name tag */}
          <section className={`kd-profile kd-st--${tone}`}>
            <span className="kd-profile-hole" aria-hidden="true" />
            <div className="kd-profile-band">
              <span>Hello, I'm</span>
              {user && validId(user.userId) && <span className="kd-member-id">#{user.userId}</span>}
            </div>
            <div className="kd-profile-main">
              <span className="kd-profile-avatar" aria-hidden="true">
                {user && user.imageUrl ? (
                  <img src={user.imageUrl} alt="" referrerPolicy="no-referrer" />
                ) : (
                  initial || <User size={44} />
                )}
              </span>
              <div className="kd-profile-info">
                <h2>{name}</h2>
                <div className="kd-profile-chips">
                  {mobile ? (
                    <a className="kd-profile-chip" href={`tel:${mobile}`}>
                      <Phone size={13} aria-hidden="true" />
                      {mobile}
                    </a>
                  ) : (
                    <span className="kd-profile-chip is-muted">
                      <Phone size={13} aria-hidden="true" />
                      No mobile
                    </span>
                  )}
                  {user && user.email ? (
                    <a className="kd-profile-chip" href={`mailto:${user.email}`}>
                      <Mail size={13} aria-hidden="true" />
                      {user.email}
                    </a>
                  ) : (
                    <span className="kd-profile-chip is-muted">
                      <Mail size={13} aria-hidden="true" />
                      No email
                    </span>
                  )}
                  <span className="kd-profile-chip">
                    <Calendar size={13} aria-hidden="true" />
                    Joined {user ? this.formatJoinDate(user.createdAt) : "—"}
                  </span>
                </div>
              </div>
              <div className="kd-profile-spent">
                <span>Spent on recent orders</span>
                <strong className="kd-price-sticker">
                  {ordersLoading ? "…" : `৳ ${spent.toLocaleString()}`}
                </strong>
              </div>
            </div>
          </section>

          {/* Tabs as stickers + content */}
          <section className="card kd-orders-card kd-profile-panel">
            <div className="kd-rf-tabs" role="tablist" aria-label="Customer details">
              {TABS.map((tab, i) => {
                const info = TAB_INFO[tab];
                const isActive = activeTab === tab;
                const loading =
                  (tab === "orders" && ordersLoading) ||
                  (tab === "preorders" && preOrdersLoading);
                return (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`kd-stat-tag kd-rf-tab kd-st--${info.tone}` + (isActive ? " is-active" : "")}
                    style={{ "--i": i }}
                    onClick={() => this.setState({ activeTab: tab })}
                  >
                    <span className="kd-stat-label">
                      <info.Icon size={13} aria-hidden="true" />
                      {info.label}
                    </span>
                    <span className="kd-stat-value">{loading ? "…" : counts[tab]}</span>
                  </button>
                );
              })}
            </div>

            <div className="kd-profile-body" role="tabpanel">
              {/* Orders */}
              {activeTab === "orders" &&
                (ordersLoading ? (
                  <LoadingRows />
                ) : orders.length === 0 ? (
                  <EmptyState>No orders from this customer yet.</EmptyState>
                ) : (
                  <>
                    <p className="kd-profile-note">Showing the 20 most recent orders.</p>
                    <ol className="kd-profile-list">
                      {orders.map((order, i) => (
                        <li
                          key={order.id}
                          className={`kd-profile-row kd-st--${STATUS_TONES[order.orderStatus] || "ink"}`}
                          style={{ "--i": Math.min(i, 12) }}
                        >
                          <button
                            type="button"
                            className="kd-id-tag kd-profile-link"
                            title="Open this order"
                            onClick={() => this.props.history.push(`/searched-order/${order.id}`)}
                          >
                            #{order.id}
                          </button>
                          <span className="kd-profile-date">
                            <Calendar size={12} aria-hidden="true" />
                            {this.formatDate(order.createdAt)}
                          </span>
                          <span className="kd-price-sticker">৳ {orderTotal(order).toLocaleString()}</span>
                          <StatusBadge status={order.orderStatus} />
                        </li>
                      ))}
                    </ol>
                  </>
                ))}

              {/* Pre-orders */}
              {activeTab === "preorders" &&
                (preOrdersLoading ? (
                  <LoadingRows />
                ) : preOrders.length === 0 ? (
                  <EmptyState>No pre-orders from this customer yet.</EmptyState>
                ) : (
                  <ol className="kd-profile-list">
                    {preOrders.map((booking, i) => (
                      <li
                        key={booking.bookingId || booking.id}
                        className={`kd-profile-row kd-st--${STATUS_TONES[booking.status] || "lilac"}`}
                        style={{ "--i": Math.min(i, 12) }}
                      >
                        <span className="kd-id-tag">#{booking.bookingId || booking.id}</span>
                        <span className="kd-profile-date">
                          <Calendar size={12} aria-hidden="true" />
                          {booking.time
                            ? new Date(booking.time).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })
                            : "—"}
                        </span>
                        <span />
                        <StatusBadge status={booking.status} />
                      </li>
                    ))}
                  </ol>
                ))}

              {/* Shipping addresses as postcards */}
              {activeTab === "addresses" &&
                (addresses.length === 0 ? (
                  <EmptyState>No saved shipping addresses.</EmptyState>
                ) : (
                  <ul className="kd-postcards">
                    {addresses.map((addr, i) => (
                      <li
                        key={addr.id || i}
                        className={"kd-postcard" + (addr.defaultShipping ? " is-default" : "")}
                        style={{ "--i": i }}
                      >
                        <span className="kd-postcard-stamp" aria-hidden="true">
                          <MapPin size={16} />
                        </span>
                        {addr.defaultShipping && <span className="kd-postcard-default">Default</span>}
                        <strong>{addr.fullName || "—"}</strong>
                        {addr.address && <span>{addr.address}</span>}
                        {(addr.district || addr.division) && (
                          <span>{[addr.district, addr.division].filter(Boolean).join(", ")}</span>
                        )}
                        {addr.mobileNo && (
                          <span className="kd-postcard-phone">
                            <Phone size={12} aria-hidden="true" />
                            {addr.mobileNo}
                          </span>
                        )}
                        {addr.addressType && (
                          <span className="kd-postcard-type">{addr.addressType}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                ))}
            </div>
          </section>
        </div>
      </Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    user: state.users.users.find(
      (user) => user.uid === ownProps.match.params.userId
    ),
  };
};

export default connect(mapStateToProps, {})(DetailUser);
